import { useEffect, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { CalendarOutlined, EditOutlined, FormOutlined, PlusOutlined, UserOutlined, CheckOutlined, DeleteOutlined, BranchesOutlined } from '@ant-design/icons';

import { Divider, Flex, Tag, Tooltip, Popover, Input, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantNavigation } from '../../../hooks/useTenantNavigation.js';
import { setNodeToBeDeleted, setSelectedStep, setWorkflow, setChangeDetected, setAddNodeInBetween, setShowCondition } from '../../../redux/slices/connectorSlice';
import { colorTagMapping, spaceColorBgMapping, workflowIconsConfig } from '../../../utils/colorBgColorMap';
import { resolveNodeTranslation, setNodeLeafForLanguage } from '../../../utils/workflowTranslations';
import SvgIcons from '../../layout/assets/SvgIcons';
import UserRoleDropdown from '../../UserRoleDropdown';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { deleteForm } from '../../../services/WorkflowServices';
import { showLoader } from '../../../utils/showloader';
// import { defaultMenuItems } from '../../../utils/createEdgeNode';

const IntermediateNode = ({ data, position, id, draggable, selectable, type, formData }) => {

    const { t, i18n } = useTranslation();
    // The inline label edits the canonical `name` in the default language, or the
    // translation for the current app language otherwise (data.translations[lng]);
    // the canonical name / status id is never touched. The approve/reject label
    // translations are still authored via the StepDetailsForm language dropdown.
    const [selectedAssignees, setSelectedAssignees] = useState([])
    const [nodeData, setNodeData] = useState({})
    // const [showAlert, setShowAlert] = useState(false)
    const [showMenu, setShowMenu] = useState(false)
    // Local state for step name so typing isn't overwritten by async Redux -> ReactFlow sync
    const [stepName, setStepName] = useState(data.name || "")
    // Track focus so the external -> local sync never overwrites in-progress typing
    // (which would reset the caret to the end of the text).
    const [isEditing, setIsEditing] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const { spaceId } = useParams();
    const hasStepColor = data.stepColor ? true : false
    let iconColor = hasStepColor ? data.stepColor : "#EB2F96"
    const isGooglePlacesNode = data.type === "googlePlaces" || data.name === "Google Places"
    const bgColor = isGooglePlacesNode ? "#EAF3FF" : hasStepColor ? spaceColorBgMapping[data.stepColor] : "#FFF0F6"

    const dispatch = useDispatch();
    const navigate = useTenantNavigation();
    const { selectedStep, workflow, changeDetected, originalWorkflow } = useSelector(state => state.connector);
    // const { spaceUsers } = useSelector(state => state.user)

    useEffect(() => {
        if (data.assignTo) {
            let reqAssignees = [];
            reqAssignees = reqAssignees.concat(data.assignTo?.user, data.assignTo?.role)
            setSelectedAssignees(reqAssignees)
        }
    }, [data])

    useEffect(() => {
        if (workflow.workflowId) {
            let currentNode = workflow.nodes.find(node => node.id === id);
            setNodeData(currentNode || {})
        }
    }, [workflow.workflowId, workflow.nodes, id])

    // Label resolved for the current app language (falls back to canonical name).
    const displayName = resolveNodeTranslation(data, i18n.language)?.name || ""

    // Keep local stepName in sync if external name/translation changes, but never
    // while the user is actively typing — the async Redux -> ReactFlow round trip
    // lags a keystroke behind and would yank the caret to the end of the text.
    useEffect(() => {
        if (!isEditing && displayName !== stepName) {
            setStepName(displayName)
        }
    }, [displayName, stepName, isEditing])

    const handleNodeClick = () => {
        dispatch(setShowCondition(false))
        dispatch(setSelectedStep({ ...data, id: id }))
        dispatch(setChangeDetected(true))
        setShowMenu(false)
    }

    const handleRemoveNode = () => {
        // e.preventDefault();
        // e.stopPropagation();
        // data.onDelete(id)
        dispatch(setNodeToBeDeleted(id))
        dispatch(setSelectedStep({}))
    }

    const saveCurrentWorkflow = (formId) => {

        let saveCurrentWorkflowEle = document.getElementById("workflowSubmit");
        if (saveCurrentWorkflowEle) {
            saveCurrentWorkflowEle.click();
            navigate("/app/space/" + spaceId + "/" + workflow.workflowId + "/" + id + "/form-editor/" + formId)

        }
    }

    const navigateToForm = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let formId = nodeData?.formData?.formId || "new"
        let nodeSaved = originalWorkflow.nodes?.find(node => node.id === id)
        if (changeDetected && formId === "new" && !nodeSaved) {
            Modal.confirm({
                title: t("Confirm"),
                content: t("You haven't saved your changes. Would you like to proceed?"),
                okText: t("Yes"),
                cancelText: t("No"),
                onOk: (event) => {
                    saveCurrentWorkflow(formId)
                    Modal.destroyAll()
                },
                footer: (_, { OkBtn, CancelBtn }) => (
                    <>
                        <CancelBtn />
                        <OkBtn />
                    </>
                ),
            });
            return
        }
        navigate("/app/space/" + spaceId + "/" + workflow.workflowId + "/" + id + "/form-editor/" + formId)
    }

    const handleIconColorChange = (key, val) => {
        let updatedNode = workflow.nodes.map(node => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, [key]: val } }
            }
            return { ...node }
        })
        dispatch(setWorkflow({ ...workflow, nodes: updatedNode }))
        dispatch(setChangeDetected(true))

    }

    const colorIconPicker = (
        <div className="color-icon-picker">
            <div className="picker-header">
                <div className="text-weight-600">{t("Pick color and icon")}</div>
            </div>
            <div className="color-row">
                {
                    ["#F5222D", "#FA8C16", "#FAAD14", "#84CC16", "#50A248", "#13C2C2", "#0A99FE", "#141BCB", "#722ED1", "#EB2F96"].map(color => {
                        return <div className="color-box" style={{ backgroundColor: color }} onClick={() => handleIconColorChange("stepColor", color)}>{data.stepColor === color && <CheckOutlined  style={{width: "16px"}} className="text-white text-sm" />}</div>
                    })
                }
            </div>
            <div className="icon-picker">
                {
                    workflowIconsConfig.map(icon => {
                        return <div className="icon-picker-box" onClick={() => handleIconColorChange("stepIcon", icon)}>
                            <span style={{ color: data.stepIcon === icon ? data.stepColor : "#00000073", display: "flex" }}>
                                <SvgIcons key={"color-" + data.stepColor} type={icon} />

                            </span>
                        </div>

                    })
                }
            </div>
        </div>
    )

    const confirmNodeDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowMenu(false)

        Modal.confirm({
            title: t('Confirm'),
            content: t('Are you sure to delete the node?'),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: (event) => {
                handleRemoveNode()
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const handleDeleteForm = async () => {
        const { nodes, edges } = workflow;
        showLoader(true)
        let deleteResponse = await deleteForm(nodeData.formData.formId)
        showLoader(false)
        if(deleteResponse.status === 200){
            let updatedNodes = nodes.map(node => {
                if(node.id === id) {
                    return {...node, formData: {formId:"", formName: ""}}
                }
                return {...node}
            })
            dispatch(setWorkflow({...workflow, nodes: updatedNodes}))
            dispatch(setChangeDetected(true))
        }
    }

    const confirmFormDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();

        Modal.confirm({
            title: t('Confirm'),
            content: t('Are you sure you want to delete this form?'),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: (event) => {
                handleDeleteForm()
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const handleAddPhase = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setShowMenu(false)

        dispatch(setAddNodeInBetween({ ...data, id: id, side: type }))
    }

    const onSelect = (values) => {
        const { nodes, edges } = workflow;
        let updatedNodes = nodes.map(node => {
            if(node.id === id) {
                return {...node, data: {...node.data, assignTo: values}}
            }
            return {...node}
        })
        dispatch(setWorkflow({nodes: updatedNodes, edges: edges}))
    }

    const handleStepCondition = (e) => {
        dispatch(setSelectedStep({...data, id: id}))
        if(data.approverType === "conditional") {
            e.preventDefault();
            e.stopPropagation();
            dispatch(setShowCondition(true))
        }
    }

    const handleNameChange = (val) => {
        setStepName(val)
        let updatedWorkflow = { ...workflow }
        let updatedNodes = updatedWorkflow.nodes.map(node => {
            if (node.id === id) {
                return { ...node, data: setNodeLeafForLanguage(node.data, i18n.language, "name", val) }
            }
            return { ...node }
        })
        updatedWorkflow.nodes = updatedNodes
        dispatch(setWorkflow({ ...updatedWorkflow }))
    }

    const handleArrowClick = (e, status) => {
        e.stopPropagation()
        setExpanded(status)
    }

    return (
        <>
            <Handle type="target" position={Position.Left} style={{ top: "24px" }} />
            <Handle type="target" position={Position.Bottom} style={{ bottom: "0" }} id="targetBottom" />

            <div className={`node-card ${selectedStep.id === id ? 'selected-node' : ""}`} onClick={handleNodeClick}>
                <Flex className={`node-header ${expanded ? "expanded-node" : ""}`} align='center' justify='space-between'>
                    <Flex justify='space-between' align='center' gap={8}>
                        <Popover content={colorIconPicker} trigger={"click"} placement="right">
                            <div className="step-icon-box icon-box cursor-pointer" style={{ backgroundColor: bgColor }}>
                                {
                                    (data.stepIcon || isGooglePlacesNode) ?
                                        <span style={{ color: data.stepColor, display: "flex" }}>
                                            <SvgIcons key={"color-" + data.stepColor} type={data.stepIcon || "location"} />
                                        </span>
                                        :
                                        <>
                                            {data.approvalStep ? <CheckOutlined style={{ color: data.stepColor || "#EB2F96" }} /> : <FormOutlined style={{ color: data.stepColor || "#EB2F96" }} />}
                                        </>
                                }

                            </div>
                        </Popover>
                        <Tooltip title={stepName} open={isEditing ? false : undefined}>
                            <Input
                                onClick={(e) => e.stopPropagation()}
                                value={stepName}
                                placeholder={t("New")}
                                onChange={(e) => handleNameChange(e.target.value)}
                                onFocus={() => setIsEditing(true)}
                                onBlur={() => setIsEditing(false)}
                                className='p-1 truncate'
                            />

                        </Tooltip>

                    </Flex>
                    {/* menu={{ items: databaseActions }} */}
                    {
                        expanded ?
                        <Flex align='center' gap={8}>
                            <ChevronDown style={{color: "gray", width: "18px", height: "18px"}} className='cursor-pointer' onClick={(e) => handleArrowClick(e, false)} />
                            <DeleteOutlined style={{ color: "#F5222D", fontSize: "14px" }} className='cursor-pointer' onClick={confirmNodeDelete} />
                        </Flex>
                        : <ChevronRight style={{color: "gray", width: "24px", height: "24px"}} className='cursor-pointer' onClick={(e) => handleArrowClick(e, true)} />
                    }
                </Flex>
                {
                    expanded ? <>
                        <Divider className='margin-0' />
                        <Flex className='node-add-button' style={{ backgroundColor: nodeData?.formData?.formId ? data.stepColor ? spaceColorBgMapping[data.stepColor] : "#fff0f6" : "#FFFF", border: nodeData?.formData?.formId ? "none" : "1px dashed #C7C8F3" }} justify={nodeData?.formData?.formId ? 'space-between' : 'center'} align='center' onClick={navigateToForm}>
                            {
                                nodeData.formData && nodeData.formData.formId ?
                                    <>
                                        <div className='node-form-title text-color-primary' >{nodeData.formData.formName}</div>
                                        <div className='flex gap-2'>
                                        <EditOutlined style={{ color: iconColor }} />
                                        <DeleteOutlined style={{ color: iconColor, width:"14px" }} onClick={confirmFormDelete} />

                                        </div>
                                    </>
                                    :
                                    <>
                                        <PlusOutlined style={{ color: "#141BCB" }} />
                                        <div className="node-form-title" >{t("Add form")}</div>
                                    </>
                            }
                        </Flex>
                        <Flex className='node-body' justify='center' align='center'>
                            <Flex className='node-form-row' justify='start' align='center' >
                                <UserOutlined style={{ color: iconColor, alignItems: (data.approverType === "default" && selectedAssignees.length > 0 ) ? "start" : "center" }} />
                                <div style={{ color: "grey" }}>
                                    {
                                        data.approverType === "conditional" ?
                                        <Flex gap={8} justify="space-between" align='center'>
                                            <span>{t("Approver")}</span>
                                        </Flex>
                                        :
                                        <>
                                            {
                                                selectedAssignees.length > 0 ?
                                                    <>
                                                        {
                                                            selectedAssignees.map(assignee => {
                                                                return <Flex gap={8} style={{marginBottom: selectedAssignees.length > 1 ? "5px" : 0}}><Tooltip title={assignee} key={"assignee-" + assignee}>

                                                                    <Tag color={colorTagMapping[iconColor]} bordered={false} >{assignee}</Tag>
                                                                </Tooltip>

                                                                </Flex>
                                                            })
                                                        }
                                                    </>
                                                    : t("Assigned To")
                                            }
                                        </>
                                    }
                                </div>
                                <Tag onClick={(e) => handleStepCondition(e)} color={colorTagMapping[iconColor]} bordered={false} style={{ textTransform: "capitalize", height: "fit-content" }}>
                                    {data.approverType === "conditional" && <BranchesOutlined style={{marginRight: "5px"}} /> }{t(data.approverType)}</Tag>

                            </Flex>
                            <Flex className='node-form-row' justify='start' align='center'>
                                <CalendarOutlined style={{ color: iconColor }} />
                                <div className='' style={{ color: "grey" }}>
                                    {
                                        data.deadline ?
                                            <span>{data.count} {t(data.period)}</span>
                                            : t("Due Date")
                                    }
                                </div>
                            </Flex>

                        </Flex>
                        <Divider className='margin-0' />
                        <Flex gap={6} vertical style={{margin: "10px 12px"}}>
                            <div className='text-color-tertiary text-weight-600 text-sm'>
                                {data.approvalStep ? t("Approver") : t("Assign To") }
                            </div>
                            <UserRoleDropdown selectedOptions={data.assignTo ? {
                                "user": data?.assignTo?.user || [],
                                "role": data?.assignTo?.role || [],
                                "group": data?.assignTo?.group || [],
                                "userPicker": data?.assignTo?.userPicker || []
                            } : { user: [], role: [], group: [], userPicker: [] }}
                                isDisabled={data.approverType === "conditional" ? true : false}
                                onSelect={onSelect}
                                tabsRequired={["user", "group", "userPicker"]}
                                getSpaceUsers={true} disabled={data?.approverType === "default" ? false : true} />
                        </Flex>
                    </>
                    : null
                }

        </div >
            <Handle type="source" position={Position.Right} style={{ top: "24px" }} />

        </>
    )

}

export default IntermediateNode
